import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiService } from "../api/apiService";
import { isPortfolioComboEngine } from "../../config/engines";
import {
    getStopDisplaySol,
    normalizeEngineGasKey,
} from "../../config/engine-gas-config";

export function normalizeStatusFetchArg(arg) {
    if (arg != null && typeof arg === "object") {
        return {
            botId: arg.botId != null && arg.botId !== "" ? String(arg.botId) : arg.botId,
            force: arg.force === true,
        };
    }
    return { botId: arg != null && arg !== "" ? String(arg) : arg, force: false };
}

function resolveEffectiveBotStatus(data) {
    const dbStatus = data?.status ?? "stopped";
    const isRunning = data?.isRunning === true;
    const stopedByAdmin = data?.stopedByAdmin === true;

    // Admin pause keeps DB status as "running" while engines are stopped.
    // Do not force "running" when the bot is already stopped (stale flag).
    if (stopedByAdmin && dbStatus === "running") {
        return "running";
    }
    if (dbStatus === "stopped" || dbStatus === "paused_by_admin") {
        return dbStatus === "paused_by_admin" ? "paused_by_admin" : "stopped";
    }
    if (isPortfolioComboEngine(data?.engine)) {
        if (
            dbStatus === "starting" ||
            dbStatus === "stopping" ||
            dbStatus === "running" ||
            dbStatus === "stopped"
        ) {
            return dbStatus;
        }
        return isRunning ? "running" : "stopped";
    }
    if (dbStatus === "starting" || dbStatus === "stopping") {
        return dbStatus;
    }
    if (dbStatus === "running") {
        return "running";
    }
    if (isRunning || data?.runningBot) {
        return "running";
    }
    return dbStatus;
}

function isDetailedToggleConflictMessage(message) {
    const lower = String(message || "").toLowerCase();
    return (
        lower.includes("standalone v8") ||
        lower.includes("cannot start combo") ||
        lower.includes("stop all v8") ||
        lower.includes("before starting the combo") ||
        lower.includes("before starting combo") ||
        lower.includes("combo segment")
    );
}

const TOGGLE_GENERIC_ERROR = "Something went wrong please try again.";
const TOGGLE_NETWORK_ERROR = "Network error. Please try again.";

/** Raw RPC / infra text must never reach the user. */
function isTechnicalToggleError(message) {
    const lower = String(message || "").toLowerCase();
    if (!lower) return false;
    return (
        lower.includes("429") ||
        lower.includes("too many requests") ||
        lower.includes("rate limit") ||
        lower.includes("rate limited") ||
        lower.includes("failed to get balance") ||
        lower.includes("operation failed after") ||
        lower.includes("last error:") ||
        lower.includes("econnreset") ||
        lower.includes("etimedout") ||
        lower.includes("econnrefused") ||
        lower.includes("fetch failed") ||
        lower.includes("socket hang up") ||
        lower.includes("network request failed") ||
        lower.includes("timeout of") ||
        lower.includes("timed out") ||
        lower.includes("blockhash") ||
        lower.includes("lamports") ||
        lower.includes("publickey") ||
        lower.includes("transaction simulation") ||
        lower.includes("sendtransactionerror") ||
        lower.includes("custom program error") ||
        lower.includes("anchorerror") ||
        lower.includes("jupiter") ||
        lower.includes("helius") ||
        lower.includes("rpc") ||
        lower.includes("stack trace") ||
        lower.includes(" at ") ||
        /0x[0-9a-f]{2,}/i.test(message) ||
        /failed to (get|fetch|validate)/i.test(message)
    );
}

function isNetworkToggleError(message) {
    const lower = String(message || "").toLowerCase();
    return (
        lower.includes("429") ||
        lower.includes("too many requests") ||
        lower.includes("rate limit") ||
        lower.includes("network") ||
        lower.includes("timeout") ||
        lower.includes("timed out") ||
        lower.includes("econnreset") ||
        lower.includes("etimedout") ||
        lower.includes("econnrefused") ||
        lower.includes("fetch failed") ||
        lower.includes("socket hang up") ||
        lower.includes("failed to get balance")
    );
}

/** Safe business messages users should still see. */
function isUserFacingToggleError(message) {
    const lower = String(message || "").toLowerCase();
    if (!lower) return false;
    if (isTechnicalToggleError(message)) return false;
    return (
        lower.includes("not enough sol") ||
        lower.includes("insufficient") ||
        lower.includes("balance") ||
        lower.includes("recharge") ||
        (lower.includes("add") && lower.includes("sol")) ||
        lower.includes("already running") ||
        lower.includes("already starting") ||
        lower.includes("already stopped") ||
        lower.includes("trial") ||
        lower.includes("access denied") ||
        lower.includes("not found") ||
        lower.includes("wallet") ||
        isDetailedToggleConflictMessage(message) ||
        lower.includes("cannot start") ||
        lower.includes("paused by admin") ||
        lower.includes("please stop") ||
        lower.includes("please wait")
    );
}

/**
 * Map start/stop API errors to short user-facing copy.
 * Technical RPC/infra messages become a generic or network retry prompt.
 */
export function getToggleErrorMessage(error, fallback) {
    const apiMsg = typeof error === "string" && error.trim() ? error.trim() : "";
    const lower = apiMsg.toLowerCase();
    const safeFallback = fallback || TOGGLE_GENERIC_ERROR;

    if (!apiMsg) return safeFallback;

    if (
        lower.includes("cannot start from its current status") ||
        lower.includes("already starting")
    ) {
        return "Bot is already starting.";
    }
    if (lower.includes("already running")) {
        if (isDetailedToggleConflictMessage(apiMsg) && !isTechnicalToggleError(apiMsg)) {
            return apiMsg;
        }
        return "Bot is already running.";
    }
    if (lower.includes("already stopped")) {
        return "Bot is already stopped.";
    }

    if (isNetworkToggleError(apiMsg) || isTechnicalToggleError(apiMsg)) {
        return isNetworkToggleError(apiMsg) ? TOGGLE_NETWORK_ERROR : safeFallback;
    }

    if (isUserFacingToggleError(apiMsg)) {
        return apiMsg;
    }

    // Unknown / opaque API text — keep it simple for users.
    if (
        lower === "failed to start bot" ||
        lower === "failed to stop bot" ||
        lower === "failed to process start request" ||
        lower === "failed to process stop request"
    ) {
        return safeFallback;
    }

    return safeFallback;
}

const STOP_STATUS_BY_CODE = {
    LOW_OWNER_SOL: "Balance is too low for trading",
    LOW_EQUITY: "Balance is too low for trading",
    RESUME_FAILED: "Bot stopped after server restart.",
    PROCESS_RESTART: "Bot stopped after server restart.",
    ZOMBIE_RECONCILED:
        "Bot was reset after a sync issue. You can start it again.",
    TRIAL_COMPLETE: "Free trial completed.",
    WORKER_FAILURE: "Stopped due to a trading worker failure.",
    MANUAL_STOP: "Stopped manually",
    ADMIN_PAUSE: "Paused by admin resume soon",
};

function normalizeLegacyStoppedReason(stoppedReason) {
    const trimmed = String(stoppedReason || "").trim();
    if (!trimmed) return null;
    const lower = trimmed.toLowerCase();
    if (
        lower.includes("owner wallet balance") ||
        lower.includes("balance too low") ||
        lower.includes("not enough sol") ||
        lower.includes("equity") ||
        lower.includes("sol equivalent") ||
        (lower.includes("too low") && lower.includes("trad"))
    ) {
        return STOP_STATUS_BY_CODE.LOW_OWNER_SOL;
    }
    if (
        lower.includes("could not resume after restart") ||
        lower.includes("server restart")
    ) {
        return STOP_STATUS_BY_CODE.RESUME_FAILED;
    }
    return trimmed;
}

export function getAdminPauseMessage(stopedByAdmin, status) {
    if (stopedByAdmin !== true) return null;
    // Stale flag on an already-stopped bot is not a live admin pause.
    if (status === "stopped" || status === "paused_by_admin") return null;
    return STOP_STATUS_BY_CODE.ADMIN_PAUSE;
}

export function getStopStatusMessage({
    stopCode,
    stoppedReason,
    status,
    stopedByAdmin,
    engine,
} = {}) {
    if (stopedByAdmin === true) {
        return null;
    }
    if (status === "paused_by_admin") {
        return STOP_STATUS_BY_CODE.ADMIN_PAUSE;
    }
    if (stopCode === "START_FAILED") {
        return null;
    }
    if (stopCode && STOP_STATUS_BY_CODE[stopCode]) {
        if (stopCode === "LOW_OWNER_SOL" || stopCode === "LOW_EQUITY") {
            const fromReason = String(stoppedReason || "").trim();
            // Prefer backend reason when it already carries live combined balance (V12 lanes).
            if (/^Balance is low for trading/i.test(fromReason)) {
                return fromReason;
            }
            if (normalizeEngineGasKey(engine)) {
                return `Balance is low for trading ~${getStopDisplaySol(engine)} SOL`;
            }
        }
        return STOP_STATUS_BY_CODE[stopCode];
    }
    return normalizeLegacyStoppedReason(stoppedReason);
}

export const fetchBotStatus = createAsyncThunk(
    'botStatus/fetchBotStatus',
    async (arg, { rejectWithValue }) => {
        const { botId } = normalizeStatusFetchArg(arg);
        try {
            const data = await apiService.getBotStatus(botId);
            const status = resolveEffectiveBotStatus(data);

            return {
                botId,
                success: true,
                status,
                isRunning: data?.isRunning === true,
                engine: data?.engine ?? null,
                stopedByAdmin: data?.stopedByAdmin === true,
                stoppedReason: data?.stoppedReason ?? null,
                stopCode: data?.stopCode ?? null,
                stoppedAt: data?.stoppedAt ?? null,
                message: data?.message ?? 'Bot status fetched successfully',
            };
        }
        catch (error) {
            return rejectWithValue({
                botId,
                message: error.message
            });
        }
    },
    {
        condition: (arg, { getState }) => {
            const { botId, force } = normalizeStatusFetchArg(arg);
            if (botId == null || botId === '') return false;
            if (force) return true;
            const entry = getState().botStatus.botStatuses[String(botId)];
            return entry?.loading !== 'pending';
        },
    },
)

const initialState = {
    botStatuses: {}, // Store status per bot ID
    botStats: {}, // Store comprehensive stats per bot ID
    botHistory: {}, // Store performance history per bot ID
    lastStatusFetchAt: {},
    loading: 'idle',
    error: null,
    message: null
}

const botStatusSlice = createSlice({
    name: 'botStatus',
    initialState,
    reducers: {
        resetBotStatus(state) {
            state.botStatuses = {};
            state.botStats = {};
            state.botHistory = {};
            state.lastStatusFetchAt = {};
            state.loading = 'idle';
            state.error = null;
            state.message = null;
        },
        clearError(state) {
            state.error = null;
        },
        clearMessage(state) {
            state.message = null;
        },
        clearBotStatus(state, action) {
            const botId = action.payload;
            if (state.botStatuses[botId]) {
                delete state.botStatuses[botId];
            }
            if (state.botStats[botId]) {
                delete state.botStats[botId];
            }
            if (state.botHistory[botId]) {
                delete state.botHistory[botId];
            }
            if (state.lastStatusFetchAt[botId]) {
                delete state.lastStatusFetchAt[botId];
            }
        },
        updateBotStats(state, action) {
            const { botId, stats } = action.payload;
            if (!state.botStats[botId]) {
                state.botStats[botId] = {};
            }
            state.botStats[botId] = { ...state.botStats[botId], ...stats };
        },
        updateBotStatusInStore(state, action) {
            const {
                botId,
                status,
                stopCode,
                stoppedReason,
                stopedByAdmin,
            } = action.payload;
            const key = botId != null && botId !== "" ? String(botId) : botId;
            if (!state.botStatuses[key]) {
                state.botStatuses[key] = {};
            }
            state.botStatuses[key].status = status;
            state.botStatuses[key].isRunning =
                status === "running" || status === "started";
            if (stopedByAdmin !== undefined) {
                state.botStatuses[key].stopedByAdmin = stopedByAdmin;
            }
            if (status === "running" || status === "starting") {
                state.botStatuses[key].stopCode = null;
                state.botStatuses[key].stoppedReason = null;
                state.botStatuses[key].stoppedAt = null;
                if (stopedByAdmin !== true) {
                    state.botStatuses[key].stopedByAdmin = false;
                }
            } else {
                if (stopCode !== undefined) {
                    state.botStatuses[key].stopCode = stopCode;
                }
                if (stoppedReason !== undefined) {
                    state.botStatuses[key].stoppedReason = stoppedReason;
                }
            }
            state.botStatuses[key].loading = 'idle';
            state.botStatuses[key].error = null;
        }
    },

    extraReducers: (builder) => {
        builder
            // Fetch bot status
            .addCase(fetchBotStatus.pending, (state, action) => {
                const { botId } = normalizeStatusFetchArg(action.meta.arg);
                state.loading = 'pending';
                state.error = null;
                state.message = null;
                state.lastStatusFetchAt[String(botId)] = Date.now();
                
                // Initialize bot status if not exists
                if (!state.botStatuses[botId]) {
                    state.botStatuses[botId] = {
                        status: null,
                        loading: 'pending',
                        error: null,
                        message: null
                    };
                } else {
                    state.botStatuses[botId].loading = 'pending';
                    state.botStatuses[botId].error = null;
                }
            })
            .addCase(fetchBotStatus.fulfilled, (state, action) => {
                const {
                    botId,
                    status,
                    message,
                    isRunning,
                    engine,
                    stopedByAdmin,
                    stoppedReason,
                    stopCode,
                    stoppedAt,
                } = action.payload;
                const key = String(botId);
                state.loading = 'idle';
                state.error = null;
                
                if (!state.botStatuses[key]) {
                    state.botStatuses[key] = {};
                }
                
                const isAdminPaused =
                    stopedByAdmin === true && status === "running";
                const retainStopMeta =
                    status === "stopped" ||
                    status === "paused_by_admin" ||
                    isAdminPaused;

                state.botStatuses[key] = {
                    status,
                    isRunning: isRunning === true,
                    engine: engine ?? null,
                    stopedByAdmin: isAdminPaused,
                    stoppedReason: retainStopMeta ? (stoppedReason ?? null) : null,
                    stopCode: retainStopMeta ? (stopCode ?? null) : null,
                    stoppedAt: retainStopMeta ? (stoppedAt ?? null) : null,
                    loading: 'idle',
                    error: null,
                    message
                };
            })
            .addCase(fetchBotStatus.rejected, (state, action) => {
                const { botId } = normalizeStatusFetchArg(action.meta.arg);
                const { message } = action.payload || { message: action.error.message };
                state.loading = 'idle';
                state.error = message;
                
                if (!state.botStatuses[botId]) {
                    state.botStatuses[botId] = {};
                }
                
                state.botStatuses[botId] = {
                    status: null,
                    loading: 'idle',
                    error: message,
                    message: null
                };
            })
            
    }
})

export const { resetBotStatus, clearError, clearMessage, clearBotStatus, updateBotStats, updateBotStatusInStore } = botStatusSlice.actions

// Enhanced selectors
export const selectBotStatus = (state, botId) => {
    const key = botId != null && botId !== "" ? String(botId) : botId;
    return state.botStatus.botStatuses[key] || { status: 'unknown', loading: 'idle', error: null };
};
export const selectBotStats = (state, botId) => state.botStatus.botStats[botId] || { loading: 'idle', error: null, stats: null };
export const selectBotHistory = (state, botId) => state.botStatus.botHistory[botId] || { loading: 'idle', error: null, history: [] };
export const selectAllBotStatuses = (state) => state.botStatus.botStatuses;
export const selectAllBotStats = (state) => state.botStatus.botStats;
export const selectBotStatusLoading = (state) => state.botStatus.loading;

export default botStatusSlice.reducer
